import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../shell';
import { MailConnectionCard } from '../organisms/MailConnectionCard';
import { SignaturesEditor } from '../organisms/SignaturesEditor';
import { useToast } from '../organisms';
import { MAIL_CONNECTION_KEY, connectErrorText, takeConnectReturnPath } from '../lib/mail';

/**
 * My email settings — /account/email (spec 0014).
 *
 * The signed-in user's own Outlook link and their signatures. Microsoft's sign-in returns here
 * with ?mail=connected or ?mail_error=<code>; a successful link made from a record's composer
 * goes straight back to that record, where the saved draft is waiting.
 */
export function MyEmailSettings() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    const ok = params.get('mail');
    const errorText = connectErrorText(params.get('mail_error'));
    if (!ok && !errorText) return;
    handled.current = true;
    queryClient.invalidateQueries({ queryKey: MAIL_CONNECTION_KEY });
    if (ok === 'connected') {
      toast({ variant: 'success', message: 'Outlook connected. Drafts you push from appealstack now land in your Outlook Drafts.' });
      const back = takeConnectReturnPath();
      if (back) { navigate(back, { replace: true }); return; }
    } else if (ok === 'admin_consent') {
      toast({ variant: 'success', message: 'Microsoft 365 consent granted for your organisation. Each person can now connect their own Outlook.' });
    } else if (errorText) {
      toast({ variant: 'error', message: errorText });
    }
    setParams({}, { replace: true });
  }, [params, setParams, navigate, queryClient, toast]);

  const h2 = { margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' } as const;
  const hint = { margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' } as const;

  return (
    <div style={{ maxWidth: 760 }}>
      <PageHeader title="My email" subtitle="Your Outlook link and your signatures" />
      <section style={{ marginTop: 'var(--sp-4)' }}>
        <h2 style={h2}>Outlook</h2>
        <p style={hint}>Email you write in appealstack is created as a draft in your own Outlook Drafts folder. You review it and press Send in Outlook, so it leaves from your address and sits in your Sent items. appealstack never sends it for you.</p>
        <MailConnectionCard />
      </section>
      <section style={{ marginTop: 'var(--sp-6)' }}>
        <h2 style={h2}>Signatures</h2>
        <p style={hint}>Your default signature is added under the email body, followed by your firm&apos;s compliance note when your firm uses one. You can pick a different signature in the composer.</p>
        <SignaturesEditor />
      </section>
    </div>
  );
}
